import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {getLockString} from '../utils/get-lock-string'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {LockedEvent$key} from './__generated__/LockedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {LockIcon} from '@primer/octicons-react'

export type TimelineEventExtraProps = {
  viewerCanPush: boolean
  repositoryOwnerLogin: string
  repositoryAvatarURL: string
}

type LockedEventProps = {
  queryRef: LockedEvent$key
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  timelineEventProps?: TimelineEventExtraProps
}

export function LockedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  timelineEventProps,
}: LockedEventProps): JSX.Element {
  const {actor, createdAt, lockReason, databaseId} = useFragment(
    graphql`
      fragment LockedEvent on LockedEvent {
        databaseId
        createdAt
        lockReason
        actor {
          ...TimelineRowEventActor
        }
      }
    `,
    queryRef,
  )
  const lockReasonString = getLockString(lockReason)
  const highlighted = String(databaseId) === highlightedEventId
  if (!timelineEventProps) return <></>

  return timelineEventProps.viewerCanPush ? (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={LockIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.locked} `}
        {lockReason && <>{`${LABELS.timeline.as} ${lockReasonString} `}</>}
        {`${LABELS.timeline.limitedToCollaborators} `}
      </TimelineRow.Main>
    </TimelineRow>
  ) : (
    <TimelineRow
      actor={null}
      highlighted={highlighted}
      refAttribute={refAttribute}
      timelineEventProps={timelineEventProps}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={LockIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.locked} `}
        {lockReason && <>{`${LABELS.timeline.as} ${lockReasonString} `}</>}
        {`${LABELS.timeline.limitedToCollaborators} `}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

try{ LockedEvent.displayName ||= 'LockedEvent' } catch {}